/**
 * DesignerStateContext — State management for the Visual Workspace Designer.
 *
 * Manages workspace definition state with undo/redo history.
 * Provides LOAD_FROM_JSON to hydrate from existing workspace definitions
 * and SERIALIZE_TO_JSON to produce the workspace JSON for saving.
 */
import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';

const DesignerStateContext = createContext(null);
const DesignerDispatchContext = createContext(null);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const generateId = (prefix = 'panel') => `${prefix}-${Date.now().toString(36)}`;

const cloneState = (state) => JSON.parse(JSON.stringify(state));

/**
 * Parse an existing workspace definition JSON into designer state.
 *
 * Views can be:
 * - Inline views (full definition with master/detail/columns)
 * - Component references: { "$ref": "component_code", "$overrides": {...}, "viewId": "...", ... }
 *
 * Component references are stored as ref-views in designer state with isRef=true.
 * The designer shows them as component reference cards rather than expandable inline views.
 */
function loadFromJson(workspace, definition) {
  const def = typeof definition === 'string' ? JSON.parse(definition) : definition;
  const views = (def?.views || []).map((v) => {
    // Check if this view is a $ref (component reference)
    if (v['$ref']) {
      return buildRefView(v);
    }
    // Inline view
    return {
      viewId: v.viewId || generateId('view'),
      label: v.label || 'Untitled',
      isDefault: !!v.default,
      isRef: false,
      panels: buildPanelsFromView(v),
      filters: v.filters || { chips: [] },
      statusBar: v.statusBar || { counters: [] },
      toolbarActions: v.toolbarActions || [],
    };
  });

  return {
    workspace: {
      id: workspace?.id || null,
      code: workspace?.workspaceCode || '',
      title: def?.title || workspace?.title || '',
      icon: def?.icon || workspace?.icon || 'ViewModule',
      domain: workspace?.domain || '',
      accessLevel: workspace?.requiredAccessLevel || 'USER',
    },
    views: views.length > 0 ? views : [createEmptyView()],
    selection: { type: 'workspace', viewId: null, panelId: null, itemId: null },
    pendingFields: [],
    pendingActions: [],
    isDirty: false,
  };
}

/**
 * Build a ref-view from a $ref JSON node.
 *
 * The ref-view stores the component code and any overrides/sibling keys.
 * When rendered, it shows as a component reference card (not an editable inline view).
 */
function buildRefView(refNode) {
  const componentCode = refNode['$ref'];
  const overrides = refNode['$overrides'] || {};

  // Collect sibling pass-through keys (anything that isn't $ref or $overrides)
  const siblings = {};
  Object.keys(refNode).forEach((key) => {
    if (key !== '$ref' && key !== '$overrides') {
      siblings[key] = refNode[key];
    }
  });

  return {
    viewId: siblings.viewId || generateId('ref'),
    label: siblings.label || overrides.label || componentCode,
    isDefault: !!(siblings.default || overrides.default),
    isRef: true,
    componentCode,
    overrides,
    siblings,
    // Ref views don't have inline panels — they reference a component
    panels: [],
    filters: { chips: [] },
    statusBar: { counters: [] },
    toolbarActions: [],
  };
}

/**
 * Convert a view's master/details structure into a flat panels array.
 */
function buildPanelsFromView(view) {
  const panels = [];

  if (view.master) {
    panels.push({
      panelId: generateId('grid'),
      type: 'grid',
      role: 'master',
      entity: view.master.entity || '',
      function: view.master.function || 'List',
      primaryKey: view.master.primaryKey || 'id',
      displayField: view.master.displayField || '',
      responseDataKey: view.master.responseDataKey || '',
      selectable: view.master.selectable || false,
      columns: (view.master.columns || []).map((c) =>
        typeof c === 'string' ? { fieldName: c, width: '', sortable: true } : c
      ),
      emptyState: view.master.emptyState || { title: '', message: '' },
    });
  }

  const details = view.details || (view.detail ? [view.detail] : []);
  details.forEach((d) => {
    panels.push({
      panelId: generateId('detail'),
      type: 'detail-grid',
      role: 'detail',
      label: d.label || 'Details',
      entity: d.entity || '',
      function: d.function || 'List',
      parentKey: d.parentKey || '',
      foreignKey: d.foreignKey || '',
      titleTemplate: d.title || '',
      columns: (d.columns || []).map((c) =>
        typeof c === 'string' ? { fieldName: c, width: '', sortable: true } : c
      ),
    });
  });

  return panels;
}

function createEmptyView() {
  return {
    viewId: generateId('view'),
    label: 'New View',
    isDefault: true,
    isRef: false,
    panels: [],
    filters: { chips: [] },
    statusBar: { counters: [] },
    toolbarActions: [],
  };
}

/**
 * Serialize designer state back to workspace JSON.
 *
 * Ref views are serialized as $ref objects: { "$ref": "code", "$overrides": {...}, ...siblings }
 * Inline views are serialized as before with master/detail/columns structure.
 */
function serializeToJson(state) {
  const views = state.views.map((v) => {
    // Ref view — serialize as $ref object
    if (v.isRef) {
      return serializeRefView(v);
    }
    // Inline view — serialize as full definition
    return serializeInlineView(v);
  });

  return {
    title: state.workspace.title,
    icon: state.workspace.icon,
    views,
  };
}

function serializeRefView(v) {
  const ref = { '$ref': v.componentCode };

  // Add $overrides if there are any (non-empty object)
  const overrides = { ...v.overrides };
  if (v.isDefault) overrides.default = true;
  if (Object.keys(overrides).length > 0) {
    ref['$overrides'] = overrides;
  }

  // Add sibling pass-through keys
  if (v.siblings) {
    Object.entries(v.siblings).forEach(([key, val]) => {
      if (key !== 'default') { // default goes in $overrides
        ref[key] = val;
      }
    });
  }

  return ref;
}

function serializeInlineView(v) {
  const masterPanel = v.panels.find((p) => p.role === 'master');
  const detailPanels = v.panels.filter((p) => p.role === 'detail');

  const view = {
    viewId: v.viewId,
    label: v.label,
    type: 'grid',
    default: v.isDefault,
  };

  if (masterPanel) {
    view.master = {
      entity: masterPanel.entity,
      function: masterPanel.function,
      primaryKey: masterPanel.primaryKey,
      displayField: masterPanel.displayField,
      ...(masterPanel.responseDataKey && { responseDataKey: masterPanel.responseDataKey }),
      ...(masterPanel.selectable && { selectable: true }),
      ...(masterPanel.emptyState?.title && { emptyState: masterPanel.emptyState }),
    };
  }

  if (detailPanels.length === 1) {
    const d = detailPanels[0];
    view.detail = {
      label: d.label,
      entity: d.entity,
      function: d.function,
      parentKey: d.parentKey,
      foreignKey: d.foreignKey,
      ...(d.titleTemplate && { title: d.titleTemplate }),
    };
  } else if (detailPanels.length > 1) {
    view.details = detailPanels.map((d) => ({
      label: d.label,
      entity: d.entity,
      function: d.function,
      parentKey: d.parentKey,
      foreignKey: d.foreignKey,
      ...(d.titleTemplate && { title: d.titleTemplate }),
    }));
  }

  if (v.filters?.chips?.length > 0) view.filters = v.filters;
  if (v.statusBar?.counters?.length > 0) view.statusBar = v.statusBar;
  if (v.toolbarActions?.length > 0) view.toolbarActions = v.toolbarActions;

  return view;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const MAX_HISTORY = 50;

function pushHistory(state, prevDesigner) {
  const history = state.history.slice(0, state.historyIndex + 1);
  history.push(cloneState(prevDesigner));
  if (history.length > MAX_HISTORY) history.shift();
  return { history, historyIndex: history.length - 1 };
}

function designerReducer(state, action) {
  const { designer } = state;

  switch (action.type) {
    // -- Initialization --
    case 'LOAD_FROM_JSON': {
      const newDesigner = loadFromJson(action.workspace, action.definition);
      return {
        designer: newDesigner,
        history: [],
        historyIndex: -1,
      };
    }

    // -- Undo / Redo --
    case 'UNDO': {
      if (state.historyIndex < 0) return state;
      const restored = cloneState(state.history[state.historyIndex]);
      return {
        ...state,
        designer: { ...restored, isDirty: true },
        historyIndex: state.historyIndex - 1,
      };
    }
    case 'REDO': {
      if (state.historyIndex >= state.history.length - 1) return state;
      const next = cloneState(state.history[state.historyIndex + 2] || state.history[state.historyIndex + 1]);
      return {
        ...state,
        designer: { ...next, isDirty: true },
        historyIndex: state.historyIndex + 1,
      };
    }

    // -- Workspace properties --
    case 'UPDATE_WORKSPACE_PROPS': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          workspace: { ...designer.workspace, ...action.props },
          isDirty: true,
        },
      };
    }

    // -- View operations --
    case 'ADD_VIEW': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      const newView = action.view || createEmptyView();
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: [...designer.views, newView],
          isDirty: true,
        },
      };
    }
    case 'ADD_REF_VIEW': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      const refView = {
        viewId: generateId('ref'),
        label: action.displayName || action.componentCode,
        isDefault: false,
        isRef: true,
        componentCode: action.componentCode,
        componentType: action.componentType,
        overrides: {},
        siblings: {},
        panels: [],
        filters: { chips: [] },
        statusBar: { counters: [] },
        toolbarActions: [],
      };
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: [...designer.views, refView],
          isDirty: true,
        },
      };
    }
    case 'UPDATE_VIEW_OVERRIDES': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) =>
            v.viewId === action.viewId
              ? { ...v, overrides: { ...v.overrides, ...action.overrides } }
              : v
          ),
          isDirty: true,
        },
      };
    }
    case 'CONVERT_TO_REF': {
      // Convert an inline view to a component reference
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) =>
            v.viewId === action.viewId
              ? {
                  ...v,
                  isRef: true,
                  componentCode: action.componentCode,
                  componentType: action.componentType || 'VIEW',
                  overrides: {},
                  siblings: {},
                  panels: [],
                }
              : v
          ),
          isDirty: true,
        },
      };
    }
    case 'DETACH_REF': {
      // Convert a ref view back to an inline view (loses the ref)
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) =>
            v.viewId === action.viewId
              ? {
                  ...v,
                  isRef: false,
                  componentCode: undefined,
                  componentType: undefined,
                  overrides: undefined,
                  siblings: undefined,
                }
              : v
          ),
          isDirty: true,
        },
      };
    }
    case 'REMOVE_VIEW': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      const filtered = designer.views.filter((v) => v.viewId !== action.viewId);
      if (filtered.length === 0) filtered.push(createEmptyView());
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: filtered,
          selection: { type: 'workspace', viewId: null, panelId: null, itemId: null },
          isDirty: true,
        },
      };
    }
    case 'UPDATE_VIEW': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) =>
            v.viewId === action.viewId ? { ...v, ...action.props } : v
          ),
          isDirty: true,
        },
      };
    }
    case 'SET_DEFAULT_VIEW': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) => ({
            ...v,
            isDefault: v.viewId === action.viewId,
          })),
          isDirty: true,
        },
      };
    }

    // -- Panel operations --
    case 'ADD_PANEL': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) =>
            v.viewId === action.viewId
              ? { ...v, panels: [...v.panels, { panelId: generateId('panel'), ...action.panel }] }
              : v
          ),
          isDirty: true,
        },
      };
    }
    case 'REMOVE_PANEL': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) =>
            v.viewId === action.viewId
              ? { ...v, panels: v.panels.filter((p) => p.panelId !== action.panelId) }
              : v
          ),
          isDirty: true,
        },
      };
    }
    case 'UPDATE_PANEL': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) =>
            v.viewId === action.viewId
              ? {
                  ...v,
                  panels: v.panels.map((p) =>
                    p.panelId === action.panelId ? { ...p, ...action.props } : p
                  ),
                }
              : v
          ),
          isDirty: true,
        },
      };
    }
    case 'REORDER_PANELS': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) =>
            v.viewId === action.viewId ? { ...v, panels: action.panels } : v
          ),
          isDirty: true,
        },
      };
    }

    // -- Column operations (grid panels) --
    case 'ADD_COLUMN': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) => ({
            ...v,
            panels: v.panels.map((p) =>
              p.panelId === action.panelId
                ? { ...p, columns: [...(p.columns || []), action.column] }
                : p
            ),
          })),
          isDirty: true,
        },
      };
    }
    case 'REMOVE_COLUMN': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) => ({
            ...v,
            panels: v.panels.map((p) =>
              p.panelId === action.panelId
                ? {
                    ...p,
                    columns: (p.columns || []).filter((c, i) =>
                      action.index !== undefined ? i !== action.index : c.fieldName !== action.fieldName
                    ),
                  }
                : p
            ),
          })),
          isDirty: true,
        },
      };
    }
    case 'UPDATE_COLUMN': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) => ({
            ...v,
            panels: v.panels.map((p) =>
              p.panelId === action.panelId
                ? {
                    ...p,
                    columns: (p.columns || []).map((c, i) =>
                      i === action.index ? { ...c, ...action.props } : c
                    ),
                  }
                : p
            ),
          })),
          isDirty: true,
        },
      };
    }
    case 'REORDER_COLUMNS': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) => ({
            ...v,
            panels: v.panels.map((p) =>
              p.panelId === action.panelId ? { ...p, columns: action.columns } : p
            ),
          })),
          isDirty: true,
        },
      };
    }

    // -- Filter operations --
    case 'ADD_FILTER_CHIP': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) =>
            v.viewId === action.viewId
              ? { ...v, filters: { chips: [...(v.filters?.chips || []), action.chip] } }
              : v
          ),
          isDirty: true,
        },
      };
    }
    case 'REMOVE_FILTER_CHIP': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) =>
            v.viewId === action.viewId
              ? {
                  ...v,
                  filters: {
                    chips: (v.filters?.chips || []).filter((_, i) => i !== action.index),
                  },
                }
              : v
          ),
          isDirty: true,
        },
      };
    }
    case 'UPDATE_FILTER_CHIP': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) =>
            v.viewId === action.viewId
              ? {
                  ...v,
                  filters: {
                    chips: (v.filters?.chips || []).map((c, i) =>
                      i === action.index ? { ...c, ...action.props } : c
                    ),
                  },
                }
              : v
          ),
          isDirty: true,
        },
      };
    }

    // -- Status bar operations --
    case 'ADD_COUNTER': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) =>
            v.viewId === action.viewId
              ? { ...v, statusBar: { counters: [...(v.statusBar?.counters || []), action.counter] } }
              : v
          ),
          isDirty: true,
        },
      };
    }
    case 'REMOVE_COUNTER': {
      const prev = cloneState(designer);
      const hist = pushHistory(state, prev);
      return {
        ...state,
        ...hist,
        designer: {
          ...designer,
          views: designer.views.map((v) =>
            v.viewId === action.viewId
              ? {
                  ...v,
                  statusBar: {
                    counters: (v.statusBar?.counters || []).filter((_, i) => i !== action.index),
                  },
                }
              : v
          ),
          isDirty: true,
        },
      };
    }

    // -- Selection --
    case 'SET_SELECTION': {
      return {
        ...state,
        designer: { ...designer, selection: action.selection },
      };
    }
    case 'CLEAR_SELECTION': {
      return {
        ...state,
        designer: {
          ...designer,
          selection: { type: 'workspace', viewId: null, panelId: null, itemId: null },
        },
      };
    }

    // -- Mark saved --
    case 'MARK_SAVED': {
      return {
        ...state,
        designer: { ...designer, isDirty: false },
      };
    }

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const initialState = {
  designer: {
    workspace: { id: null, code: '', title: '', icon: 'ViewModule', domain: '', accessLevel: 'USER' },
    views: [createEmptyView()],
    selection: { type: 'workspace', viewId: null, panelId: null, itemId: null },
    pendingFields: [],
    pendingActions: [],
    isDirty: false,
  },
  history: [],
  historyIndex: -1,
};

export function DesignerStateProvider({ children }) {
  const [state, dispatch] = useReducer(designerReducer, initialState);

  return (
    <DesignerStateContext.Provider value={state}>
      <DesignerDispatchContext.Provider value={dispatch}>
        {children}
      </DesignerDispatchContext.Provider>
    </DesignerStateContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useDesignerState() {
  const ctx = useContext(DesignerStateContext);
  if (!ctx) throw new Error('useDesignerState must be used within DesignerStateProvider');
  return ctx.designer;
}

export function useDesignerDispatch() {
  const ctx = useContext(DesignerDispatchContext);
  if (!ctx) throw new Error('useDesignerDispatch must be used within DesignerStateProvider');
  return ctx;
}

export function useDesignerHistory() {
  const ctx = useContext(DesignerStateContext);
  return {
    canUndo: ctx.historyIndex >= 0,
    canRedo: ctx.historyIndex < ctx.history.length - 1,
  };
}

export function useSelectedView() {
  const designer = useDesignerState();
  const { selection, views } = designer;
  if (!selection.viewId) return views[0] || null;
  return views.find((v) => v.viewId === selection.viewId) || views[0] || null;
}

export function useSelectedPanel() {
  const designer = useDesignerState();
  const { selection, views } = designer;
  if (!selection.panelId) return null;
  for (const v of views) {
    const panel = v.panels.find((p) => p.panelId === selection.panelId);
    if (panel) return panel;
  }
  return null;
}

export { serializeToJson, loadFromJson };
